import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getMyPunchReceiptPdfApi, listMyPunchReceiptsApi, MyPunchReceipt } from "../api/meApi";
import EmployeeShell from "../components/EmployeeShell";
import { COLABORADOR_HOME } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";

import "./Colaborador.css";

const TZ = "America/Sao_Paulo";
const dayLabel = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit", timeZone: TZ });
const dayKey = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: TZ });

/**
 * Comprovantes de marcação do próprio colaborador (Portaria 671, art. 80): cada
 * marcação feita no quiosque da loja tem o seu, disponível aqui logo depois de
 * marcar, pelos últimos 60 dias (a Portaria pede no mínimo 48 horas).
 */
export default function MyPunchReceiptsPage() {
  const goBack = useGoBack(COLABORADOR_HOME());
  const [items, setItems] = useState<MyPunchReceipt[] | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    listMyPunchReceiptsApi()
      .then(setItems)
      .catch((error: any) => {
        toast.error(error?.message || "Não foi possível carregar os seus comprovantes.");
        setItems([]);
      });
  }, []);

  // Por dia, do mais recente (a lista já vem ordenada).
  const byDay = useMemo(() => {
    const groups: { key: string; label: string; items: MyPunchReceipt[] }[] = [];
    for (const item of items ?? []) {
      const key = dayKey(item.punchedAt);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(item);
      else groups.push({ key, label: dayLabel(item.punchedAt), items: [item] });
    }
    return groups;
  }, [items]);

  const open = async (item: MyPunchReceipt) => {
    // A aba é aberta ANTES do await: aberta depois, o bloqueador de pop-up a barra.
    const tab = window.open("", "_blank");
    setOpening(item.id);
    try {
      const url = URL.createObjectURL(await getMyPunchReceiptPdfApi(item.id));
      if (tab) {
        tab.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = `comprovante-ponto-nsr-${item.nsr}.pdf`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      tab?.close();
      toast.error(error?.message || "Não foi possível abrir o comprovante.");
    } finally {
      setOpening(null);
    }
  };

  return (
    <EmployeeShell>
      <section className="colabPanel">
        <div className="colabPageTitle">
          <h2 className="colabPanel__title" style={{ margin: 0 }}>
            Meus comprovantes
          </h2>
          <button type="button" className="colabBtn colabBtn--ghost" onClick={goBack}>
            Voltar
          </button>
        </div>

        {items === null ? (
          <p className="colabLoading">Carregando...</p>
        ) : items.length === 0 ? (
          <p className="colabHint">
            Nenhuma marcação nos últimos 60 dias. O comprovante aparece aqui logo depois de você marcar o ponto no aparelho da loja.
          </p>
        ) : (
          byDay.map((group) => (
            <div key={group.key}>
              <h3 className="colabPanel__title" style={{ fontSize: 16, margin: "16px 0 8px", textTransform: "capitalize" }}>
                {group.label}
              </h3>
              <div className="colabList">
                {group.items.map((item) => (
                  <article key={item.id} className="colabListItem">
                    <div className="colabListItem__head">
                      <span>{timeLabel(item.punchedAt)}</span>
                      <strong>NSR {String(item.nsr).padStart(9, "0")}</strong>
                    </div>
                    <p className="colabListItem__meta">Código {item.hashShort}…</p>
                    <button
                      type="button"
                      className="colabBtn colabBtn--primary colabBtn--compact"
                      onClick={() => open(item)}
                      disabled={opening !== null}
                      style={{ marginTop: 8 }}
                    >
                      {opening === item.id ? "Abrindo..." : "Abrir comprovante"}
                    </button>
                  </article>
                ))}
              </div>
            </div>
          ))
        )}

        <p className="colabFooterNote">
          Comprovante de Registro de Ponto do Trabalhador. Se uma marcação estiver errada ou faltando, peça a correção em Meus pedidos.
        </p>
      </section>
    </EmployeeShell>
  );
}
